/**
 * O formato do que a caixa-preta grava. Puro: sem three, sem ws, sem DOM.
 *
 * Existe como pacote, e NEVER como arquivo dentro de um dos apps, porque o
 * cliente ESCREVE e o servidor LÊ o mesmo objeto. Um formato definido de um lado
 * só vira dois formatos no dia em que alguém acrescenta um campo — e o dado já
 * gravado passa a ser ilegível sem que nada quebre em compilação.
 *
 * ## O que este arquivo NEVER pode ganhar
 *
 * Nada que identifique pessoa. NEVER `guestToken`, NEVER e-mail, NEVER endereço,
 * NEVER conteúdo digitado. O coletor ainda passa tudo pelo `scrub` do servidor
 * como segunda linha de defesa, mas a primeira é NEVER escrever o campo aqui.
 *
 * ## Por que os eventos são estes
 *
 * Cada um existe para responder uma pergunta ABERTA, e NEVER por completude.
 * Evento que ninguém vai consultar é banda do jogador e ruído na consulta.
 */

/**
 * ALWAYS incremente ao mudar o formato. O dado antigo continua no disco.
 *
 * `kind` NOVO NEVER sobe versão, e campo novo em `kind` que já existe SOBE. A
 * diferença é o que o dado meio-preenchido faz com a consulta: bundle velho que
 * NEVER conhece um `kind` manda ZERO linha dele, e zero linha é honesto — o
 * `buildSha` de cada linha diz qual build mediu o quê. Campo faltando dentro de
 * um evento que a consulta lê é que vira soma de zeros com cara de medida.
 *
 * v5: o lote ganhou `tabId` e `run`, e `session.start` ganhou `resumedBy` — a
 * ABA e a RODADA dentro dela. Sobe de versão pelo mesmo motivo dos outros: os
 * três são obrigatórios, e bundle antigo mandaria lote sem eles. Existe porque
 * `jogar de novo` chama `location.reload()`, e o `clientId` é sorteado a cada
 * abertura: sem estes campos, quem toca o botão chega como PESSOA NOVA, e a
 * medição do Portão 02 — segunda partida VOLUNTÁRIA — NEVER é possível.
 * Medido na marra em `T-9G8`, onde ela NEVER saiu. Ver `ADR-0023`.
 *
 * v4: `match.end` ganhou `code` — COMO a partida acabou, e NEVER só que acabou.
 * Sobe de versão pelo mesmo motivo do v2: o campo é OBRIGATÓRIO, e bundle antigo
 * na mão de quem NEVER recarregou mandaria `match.end` sem ele. Sem subir, esse
 * evento cairia sozinho e em silêncio, e o laudo mostraria uma sessão em que
 * ninguém terminou partida nenhuma — que é pior que lote recusado com motivo.
 *
 * v3: os eventos do ímã ganharam `pointerId` e `activePointers`. MEDIDO em
 * `T-DXX`: o campo cai em ciclo REGULAR de ~98 ms preso / ~87 ms solto, com
 * `cause: up` e NEVER `cancel` — ou seja, o browser está fechando e reabrindo o
 * toque. Sem saber QUAL ponteiro e QUANTOS dedos estão na tela, NEVER dá para
 * separar "o mesmo dedo pisca" de "o outro dedo derruba este".
 *
 * v2: `net.sample` ganhou o estado do RELÓGIO de interpolação. Subiu de versão em
 * vez de aceitar os dois formatos porque bundle antigo continua na mão de quem
 * NEVER recarregou, e lote meio-preenchido misturado com lote inteiro faria a
 * consulta somar zeros como se fossem medidas.
 */
export const TELEMETRY_SCHEMA_VERSION = 5;

/**
 * Como esta abertura de página começou. `fresh` é a primeira da aba; `retry` veio
 * do botão `jogar de novo`; `reload` é recarga sem o botão.
 */
export type SessionResume = 'fresh' | 'retry' | 'reload';

/** Por que o campo magnético caiu. É a pergunta `ímã u/c/b` do `WIP`. */
export type MagnetReleaseCause = 'up' | 'cancel' | 'blur';

/**
 * Comum a todo evento. Preenchido pelo cliente uma vez e copiado em cada linha:
 * NDJSON é lido linha a linha, e uma linha que precisa de outra para ser
 * entendida NEVER sobrevive a um filtro.
 */
export interface EventBase {
  /** Milissegundos desde o começo desta sessão de cliente. NEVER relógio de parede. */
  readonly t: number;
}

/**
 * O campo magnético caiu, e por quê.
 *
 * `cancel` é o achado que o `WIP` está esperando: significa que o BROWSER tomou o
 * ponteiro no meio do gesto — o dedo NEVER soltou. Se ele aparecer, o conserto é
 * de `touch-action`/captura de ponteiro, e NEVER da lógica do ímã.
 */
export interface MagnetReleaseEvent extends EventBase {
  readonly kind: 'input.magnet_release';
  readonly cause: MagnetReleaseCause;
  /** Quanto tempo o botão ficou segurado antes de cair, em ms. */
  readonly heldMs: number;
  /**
   * Qual ponteiro soltou. Casado com o `pointerId` do `input.magnet_down`, ele
   * separa as duas hipóteses que sobraram: se o id MUDA a cada ciclo, o browser
   * está fechando e reabrindo o toque; se REPETE, é o mesmo dedo piscando.
   */
  readonly pointerId: number;
  /** Dedos na tela no instante da queda. Acima de 1, o outro dedo é suspeito. */
  readonly activePointers: number;
}

/**
 * O botão do ímã foi PRESSIONADO. Existe só desde `T-DXX`, e por um motivo:
 * a queda sozinha NEVER conta a história. O código bloqueia repetição
 * (`onMagnetDown` sai cedo se já há ponteiro), então para cada queda medida tem
 * que existir um `pointerdown` NOVO no botão — e é o par que diz se o dedo saiu
 * e voltou, ou se o browser inventou o ciclo.
 */
export interface MagnetDownEvent extends EventBase {
  readonly kind: 'input.magnet_down';
  readonly pointerId: number;
  /** `touch`, `mouse` ou `pen`. Num celular, qualquer coisa fora de `touch` é pista. */
  readonly pointerType: string;
  readonly activePointers: number;
  /** `setPointerCapture` pegou. Falso significa que o fim do toque pode se perder. */
  readonly captured: boolean;
  /** Desde a queda ANTERIOR, em ms. É o "solto" do ciclo que estamos caçando. */
  readonly sinceReleaseMs: number;
}

/**
 * Um corpo trocou de base de tempo (`ADR-0017`).
 *
 * `seamUnits` é o salto que o jogador teria visto se `BodyHandoff` NEVER
 * existisse. É o número que decide se `BUD-body-handoff` (200 ms, hipótese, NEVER
 * medida) está certo — e é exatamente a pergunta que hoje depende de o usuário
 * olhar a tela e dizer "pulou".
 */
export interface BodyHandoffEvent extends EventBase {
  readonly kind: 'body.handoff';
  readonly bodyId: number;
  readonly from: 'prev' | 'interp';
  readonly to: 'prev' | 'interp';
  readonly seamUnits: number;
  readonly windowMs: number;
}

/**
 * UM quadro que estourou o limiar. É o par de `frame.window`, e a diferença
 * entre os dois é a pergunta: a janela diz QUANTO, e este diz DE QUEM.
 *
 * MEDIDO em `T-DMA`: pior 1% de 173 ms num celular, tudo nos primeiros 20 s. A
 * janela NEVER tem como dizer se aqueles 173 ms foram o nosso desenho ou o
 * browser fazendo outra coisa (decodificar asset, compilar shader, coletar
 * lixo) — e o conserto é diferente em cada caso.
 *
 * `drawMs` é o desenho do quadro ANTERIOR de propósito: `frameMs` é o intervalo
 * entre duas chamadas do `requestAnimationFrame`, e o que cabe DENTRO desse
 * intervalo é o desenho anterior. Casar com o desenho deste quadro seria
 * comparar o tempo com trabalho que ainda NEVER aconteceu.
 *
 * `drawMs` perto de `frameMs` é NOSSO, e a fatia seguinte parte o desenho em
 * sim e render. `drawMs` pequeno com `frameMs` grande é de FORA, e partir o
 * desenho teria sido trabalho jogado fora. Dois números decidem qual dos dois,
 * e é por isso que só existem dois.
 */
export interface FrameSpikeEvent extends EventBase {
  readonly kind: 'frame.spike';
  /** Intervalo entre dois quadros, em ms. É o que o jogador sentiu como travada. */
  readonly frameMs: number;
  /** Quanto o NOSSO desenho levou no quadro anterior, em ms. */
  readonly drawMs: number;
  /** Escala de resolução em vigor. Pico com escala já baixa NEVER é falta de GPU. */
  readonly scale: number;
  /**
   * O quantos-ésimo pico desta sessão, a partir de 1. Existe por causa do teto:
   * o cliente para de MANDAR depois de um número de picos, e sem isto NEVER
   * daria para saber se 12 picos são 12 ou 300. O maior `index` do laudo é um
   * PISO, e NEVER a conta.
   */
  readonly index: number;
}

/** Uma janela de quadros fechada. p95 e pior 1%, NEVER média (PERFORMANCE M3). */
export interface FrameWindowEvent extends EventBase {
  readonly kind: 'frame.window';
  readonly frames: number;
  readonly p95Ms: number;
  readonly worst1PercentMs: number;
  readonly maxMs: number;
  readonly tier: string;
  readonly renderScale: number;
}

/**
 * Como a rede está sendo VISTA pelo aparelho, e o estado do RELÓGIO que desenha os
 * outros. Os dois juntos porque a queixa de "lag" pode vir de qualquer um, e
 * separá-los em dois eventos obrigaria a casar por tempo na consulta.
 *
 * MEDIDO em 5 celulares (`docs/testes/2026-08-15-o-ima-...`): o RTT estava bom
 * (22–60 ms) e o buffer estourou `BUD-interp-max` em TODO aparelho, até 15 686 ms —
 * 78× o teto. Sem os contadores abaixo NEVER dá para saber se o relógio tenta se
 * salvar e falha, ou se NEVER tenta: são consertos diferentes.
 */
export interface NetSampleEvent extends EventBase {
  readonly kind: 'net.sample';
  readonly rttMs: number;
  /** Distância que a última reconciliação corrigiu, em unidades de mundo. */
  readonly reconcileUnits: number;
  /** Atraso que o cliente TEM. Contra `interpTargetMs`, diz se o relógio fugiu. */
  readonly interpBufferMs: number;
  /**
   * Atraso que o cliente PEDE, entre `BUD-interp` e `BUD-interp-max`. Se ele estiver
   * no teto e o buffer em 15 s, quem fugiu foi o RELÓGIO, e NEVER a adaptação.
   */
  readonly interpTargetMs: number;
  /** Vezes que o buffer SECOU e teve que ser refeito. Cada uma é um solavanco visto. */
  readonly reanchors: number;
  /**
   * Vezes que o relógio SALTOU para a frente por estar atrasado demais.
   * Zero com o buffer em 15 s significa que o salvamento NEVER dispara — que é a
   * hipótese principal, e a que este campo existe para confirmar ou matar.
   */
  readonly snaps: number;
  /** Ritmo do relógio. 1 é tempo real; o desvio é limitado por `BUD-interp-rate`. */
  readonly interpRate: number;
  /** Snapshots recusados por chegarem fora de ordem. Sintoma de rede, NEVER erro. */
  readonly stale: number;
}

/**
 * Abertura da sessão. O único evento com dado de APARELHO, e ele é grosso de
 * propósito: tela, memória e núcleos explicam quadro baixo; modelo exato NEVER
 * explica mais e aproxima de identificar pessoa.
 */
export interface SessionStartEvent extends EventBase {
  readonly kind: 'session.start';
  /**
   * De ONDE veio esta abertura. É o campo do Portão 02, e NEVER um detalhe:
   * `retry` só existe porque o botão `jogar de novo` o escreveu ANTES de
   * recarregar, então ele mede INTENÇÃO, e NEVER recarga. `reload` é a mesma aba
   * aberta de novo por qualquer outro motivo (F5, o browser restaurando a aba), e
   * misturar os dois transformaria o portão num número de apoio.
   */
  readonly resumedBy: SessionResume;
  readonly screenW: number;
  readonly screenH: number;
  readonly dpr: number;
  readonly cores: number;
  readonly tier: string;
  /** `pointer: coarse` — dedo, e NEVER mouse. Muda a leitura de todo evento de toque. */
  readonly coarsePointer: boolean;
}

/** O que toda linha de partida carrega. É o que amarra o cliente ao replay do servidor. */
interface MatchEventBase extends EventBase {
  readonly matchId: string;
  readonly playerId: number;
  readonly seats: number;
}

/** Entrou numa partida. NEVER tem `code`: entrar NEVER tem desfecho. */
export interface MatchJoinEvent extends MatchEventBase {
  readonly kind: 'match.join';
}

/**
 * Saiu de uma partida, e COMO. O `code` é o `reject.code` do servidor
 * (`REJECT_CODES`, `packages/protocol`): 8 é vitória, e NEVER é o mesmo desfecho
 * que tempo esgotado.
 *
 * Existe porque sem ele o desfecho da partida só mora no LOG do servidor, e
 * laudo de sessão que depende de `grep` em VPS NEVER vira rotina. Quem lê o
 * número é `scripts/probe/queries/07-desfecho.sql`.
 *
 * O campo é do CLIENTE e mede o que o cliente VIU — a autoridade continua sendo
 * o servidor (invariante 2). Divergência entre os dois é dado, e NEVER erro de
 * medição: quer dizer que a tela mostrou um fim que a sala NEVER decidiu.
 */
export interface MatchEndEvent extends MatchEventBase {
  readonly kind: 'match.end';
  readonly code: number;
}

export type MatchEvent = MatchJoinEvent | MatchEndEvent;

/**
 * O `code` de quando a sala NEVER decidiu nada: o fio caiu, e o cliente saiu sem
 * receber `reject`. Ocupa o 0, que `REJECT_CODES` NEVER usa.
 *
 * É uma categoria de desfecho como qualquer outra, e a mais importante de saber
 * contar: sessão em 4G onde metade das partidas termina em 0 NEVER é sessão sobre
 * dificuldade, é sessão sobre rede.
 */
export const MATCH_END_NO_CODE = 0;

/**
 * A fila descartou evento. Existe porque fila silenciosa é dado que MENTE: sem
 * esta linha, "o ímã caiu 3 vezes" pode significar 3 ou 300, e NEVER há como
 * saber qual dos dois.
 */
export interface DroppedEvent extends EventBase {
  readonly kind: 'queue.dropped';
  readonly dropped: number;
}

export type TelemetryEvent =
  | MagnetReleaseEvent
  | MagnetDownEvent
  | BodyHandoffEvent
  | FrameWindowEvent
  | FrameSpikeEvent
  | NetSampleEvent
  | SessionStartEvent
  | MatchEvent
  | DroppedEvent;

export type EventKind = TelemetryEvent['kind'];

/**
 * O lote que vai no `POST /t`. O contexto vive AQUI e NEVER em cada evento: ele
 * repete em todas as linhas, e repetir 8 campos por evento multiplicaria a banda
 * do jogador por nada. O coletor é quem redistribui para cada linha no disco.
 */
export interface TelemetryBatch {
  readonly v: number;
  /**
   * Código da sessão de TESTE (`?t=T-4F2`). Sem ele o coletor RECUSA o lote:
   * gravar só teste marcado é a decisão de escopo, e ela é aplicada no servidor
   * porque cliente NEVER é autoridade de nada (invariante 2).
   */
  readonly test: string;
  /**
   * Id opaco desta ABERTURA de página. NEVER é o `guestToken`, e NEVER sobrevive
   * ao recarregar — quem sobrevive é o `tabId`.
   */
  readonly clientId: string;
  /**
   * Id opaco da ABA, e ele SOBREVIVE ao recarregar. Mora em `sessionStorage`,
   * que morre quando a aba fecha: isso é o que separa MEDIR de RASTREAR — ele
   * junta as rodadas de uma sessão de teste, e NEVER reconhece ninguém amanhã.
   */
  readonly tabId: string;
  /** Qual rodada desta aba, a partir de 1. Cresce a cada abertura da mesma aba. */
  readonly run: number;
  readonly build: { readonly sha: string; readonly time: string; readonly env: string };
  readonly matchId: string | null;
  readonly playerId: number | null;
  readonly events: readonly TelemetryEvent[];
}

/** Formato do código de sessão de teste: `T-` e 3 caracteres de base32 legível. */
export const TEST_CODE_PATTERN = /^T-[0-9A-HJ-NP-Z]{3}$/;

export function isTestCode(value: unknown): value is string {
  return typeof value === 'string' && TEST_CODE_PATTERN.test(value);
}
